import { z } from "zod";

/**
 * WhatsApp Widget app — the locked cross-layer contract.
 *
 * A floating WhatsApp bubble on the agency's storefront. Tapping it opens a
 * pop-up listing up to three team members; tapping a member opens WhatsApp
 * addressed to their number with a pre-written greeting.
 *
 * Two shapes live here:
 *   - `whatsappWidgetSettingsSchema` — what the operator saves (raw, with
 *     placeholders intact). The operator form binds to it, the operator-api
 *     re-validates on save.
 *   - `whatsappWidgetViewSchema` — what the storefront receives for ONE page,
 *     with placeholders already resolved and the chat link built. Kept here
 *     rather than in `dtos/traveler/` to avoid an apps↔dtos import cycle,
 *     mirroring `noticeBarResponseSchema`.
 */

/** The pop-up stays quick to scan and enquiries stay with accountable people. */
export const WHATSAPP_WIDGET_MAX_MEMBERS = 3;

/**
 * Digits with at most one leading `+`, as the operator field enforces while
 * typing. `wa.me` addresses a number by digits alone, so anything else (spaces,
 * dashes, letters) produces a link that dead-ends — rejected on save (→ 422)
 * rather than silently swallowing a traveler's message.
 *
 * Exported because the Tour Page's own WhatsApp number is a different number
 * for a different surface but the same KIND of value, and `wa.me` is just as
 * unforgiving there. Two hand-written regexes would drift the day one is
 * loosened.
 */
export const WHATSAPP_NUMBER_PATTERN = /^\+?\d{6,20}$/;

/** The one message the two WhatsApp surfaces share a cap with. Comfortably
 *  under WhatsApp's own prefill limit once the tour name and a full URL are
 *  substituted in. */
export const WHATSAPP_MESSAGE_MAX = 600;

/**
 * Placeholders an operator can drop into a greeting message.
 *
 * They resolve against whatever the traveler is looking at — see
 * {@link WhatsappMessageSubject}. The names stay tour-flavoured because that is
 * the page they were designed for and the one an operator writes them on; a
 * rename would break every message already saved.
 */
export const WHATSAPP_TOKEN_TOUR_NAME = "{tour_name}";
export const WHATSAPP_TOKEN_TOUR_URL = "{tour_url}";
/**
 * The departure a traveler tapped, on a tour page's date list. There is no date
 * anywhere else, so off a departure row this resolves to nothing rather than to
 * a guess.
 */
export const WHATSAPP_TOKEN_DATE = "{date}";

const teamMemberSchema = z.object({
  name: z.string().trim().min(1, "Add a name").max(60),
  role: z.string().trim().min(1).max(60).optional(),
  number: z
    .string()
    .trim()
    .regex(
      WHATSAPP_NUMBER_PATTERN,
      "Include the country code, digits only — e.g. +12345678900",
    ),
  /**
   * Blank is valid: WhatsApp then opens an empty chat with that member. The cap
   * is well under WhatsApp's own prefill limit so a resolved message (tour name
   * and full URL substituted in) still fits.
   */
  message: z.string().trim().max(WHATSAPP_MESSAGE_MAX).default(""),
  /**
   * A public URL from the agency's own media library. Absent → the storefront
   * renders the member's initials on a coloured circle.
   */
  avatarUrl: z
    .string()
    .trim()
    .url("Pick a photo from your media library")
    .max(2048)
    .refine((u) => /^https?:\/\//i.test(u), {
      message: "Use an http:// or https:// image URL",
    })
    .optional(),
});
export type WhatsappTeamMember = z.infer<typeof teamMemberSchema>;

/**
 * Corner distances, in CSS pixels. Bounded so the bubble can't be pushed off
 * screen or parked over the Book button; the settings form clamps to the same
 * range, so an out-of-range value only reaches the API from a hand-crafted
 * request.
 */
const cornerDistanceSchema = z.number().int().min(0).max(200);

export const whatsappWidgetSettingsSchema = z.object({
  /**
   * Empty is valid — an installed-but-unconfigured app. The storefront then
   * reports no widget rather than an empty pop-up.
   */
  members: z
    .array(teamMemberSchema)
    .max(
      WHATSAPP_WIDGET_MAX_MEMBERS,
      `Up to ${WHATSAPP_WIDGET_MAX_MEMBERS} team members`,
    )
    .default([]),
  buttonLabel: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .default("Need help? Chat with us"),
  /** Which side of the viewport the bubble sits on. */
  position: z.enum(["right", "left"]).default("right"),
  offsetX: cornerDistanceSchema.default(20),
  offsetY: cornerDistanceSchema.default(20),
  /**
   * The greeting sent from pages with no tour on them — home, blog,
   * destinations. Agency-wide rather than per member: a member's own `message`
   * is written around the tour placeholders, and there is no tour on those
   * pages for them to resolve against.
   *
   * Blank is rejected, absent is defaulted. The operator form binds this field
   * and so cannot save it empty; an install saved before the field existed has
   * no key at all, and failing that install would pull a live widget off the
   * storefront instead of prompting anyone to write a better line.
   */
  offTourMessage: z
    .string()
    .trim()
    .min(1, "Write the message travelers send from your other pages")
    .max(WHATSAPP_MESSAGE_MAX)
    .default("Hi! I have a question about your tours. Could you help?"),
  popupTitle: z.string().trim().min(1).max(60).default("Start a Conversation"),
  popupSubtitle: z.string().trim().max(160).default(""),
  responseTimeNote: z.string().trim().max(160).default(""),
  showOnDesktop: z.boolean().default(true),
  showOnMobile: z.boolean().default(true),
});
export type WhatsappWidgetSettings = z.infer<
  typeof whatsappWidgetSettingsSchema
>;

const TOUR_TOKEN_PATTERN = /\{tour_name\}|\{tour_url\}|\{date\}/g;

/**
 * What the greeting placeholders resolve against: the tour the traveler was
 * looking at when they tapped.
 *
 * Only ever a tour. Off a tour page there is nothing for `{tour_name}` to name,
 * so those pages send `offTourMessage` verbatim rather than substituting the
 * agency into a sentence that was written about a trip.
 */
export interface WhatsappMessageSubject {
  name: string;
  url: string;
  /** Already formatted for display — the storefront formats in the agency's
   *  locale before calling, because this module holds no locale. */
  date?: string;
}

/**
 * Resolve the placeholders in a greeting message.
 *
 * ONE pass over the original string. Replacing the tokens in sequence would
 * re-scan the text already substituted in, so a subject whose name contained
 * `{tour_url}` would have that expanded too. A function replacement also means
 * a `$&` in a tour title is inserted literally.
 */
export function resolveWhatsappMessage(
  message: string,
  subject: WhatsappMessageSubject,
): string {
  return message.replace(TOUR_TOKEN_PATTERN, (token) => {
    if (token === WHATSAPP_TOKEN_TOUR_NAME) return subject.name;
    if (token === WHATSAPP_TOKEN_TOUR_URL) return subject.url;
    return subject.date ?? "";
  });
}

/**
 * The `wa.me` deep link for a member. WhatsApp addresses a number by digits
 * only, so the stored `+` is dropped; an empty message yields a bare chat link.
 */
export function whatsappChatUrl(number: string, message: string): string {
  const digits = number.replace(/\D/g, "");
  const base = `https://wa.me/${digits}`;
  return message.length > 0
    ? `${base}?text=${encodeURIComponent(message)}`
    : base;
}

/** One member as the storefront renders them, for one specific page. */
export const whatsappWidgetMemberViewSchema = z.object({
  name: z.string(),
  role: z.string().optional(),
  number: z.string(),
  avatarUrl: z.string().optional(),
  /** `message` with the tour placeholders already substituted. */
  message: z.string(),
  /** Ready-to-render `https://wa.me/…` link carrying `message`. */
  chatUrl: z.string(),
});
export type WhatsappWidgetMemberView = z.infer<
  typeof whatsappWidgetMemberViewSchema
>;

/**
 * The widget for one page. `members` is non-empty by construction — "no team"
 * is expressed as a null widget, not an empty list, so the storefront has one
 * thing to check.
 *
 * `offTourMessage` does not travel: by this point it has already been folded
 * into each member's `message` (or the tour greeting has), so shipping it too
 * would put a second, unused copy of the greeting on the wire.
 */
export const whatsappWidgetViewSchema = whatsappWidgetSettingsSchema
  .omit({ members: true, offTourMessage: true })
  .extend({ members: z.array(whatsappWidgetMemberViewSchema).min(1) });
export type WhatsappWidgetView = z.infer<typeof whatsappWidgetViewSchema>;

/**
 * What the traveler feed returns. `null` means there is nothing to show: the app
 * is not installed, it has no team members, or it is switched off for both
 * desktop and mobile.
 */
export const whatsappWidgetResponseSchema = z.object({
  whatsappWidget: whatsappWidgetViewSchema.nullable(),
});
export type WhatsappWidgetResponse = z.infer<
  typeof whatsappWidgetResponseSchema
>;

/**
 * Settings + what the traveler is looking at → what the storefront renders, or
 * `null` when there is nothing to show. The single place the widget's "should it
 * appear" rule and its placeholder resolution live, so operator preview,
 * traveler-api, and the storefront can never disagree.
 *
 * `subject` is the tour the traveler is on, or `null` off one — where every
 * member sends the agency-wide `offTourMessage` instead of their own greeting.
 */
export function buildWhatsappWidgetView(
  settings: WhatsappWidgetSettings,
  subject: WhatsappMessageSubject | null,
): WhatsappWidgetView | null {
  const { members, offTourMessage, ...chrome } = settings;
  if (members.length === 0) return null;
  if (!chrome.showOnDesktop && !chrome.showOnMobile) return null;
  return {
    ...chrome,
    members: members.map((member) => {
      const message = subject
        ? resolveWhatsappMessage(member.message, subject)
        : offTourMessage;
      return {
        name: member.name,
        ...(member.role ? { role: member.role } : {}),
        number: member.number,
        ...(member.avatarUrl ? { avatarUrl: member.avatarUrl } : {}),
        message,
        chatUrl: whatsappChatUrl(member.number, message),
      };
    }),
  };
}
